'use client';

import { useTranslations } from 'next-intl';
import type { FC, ReactElement } from 'react';
import { cloneElement, useEffect, useMemo, useState } from 'react';
import { ProductLineApply1 } from '@/app/components/icons/ProductLineApply1';
import { BackfillTargetOption } from '@/app/components/shared/BackfillTargetOption';
import {
  HoverCard,
  HoverCardArrow,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from '@/app/components/ui/hover-card';
import { useDevice } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { CreationInfo, CreationWithTask, ToolTaskFormVideoPrompt } from '@/app/types';
import { BackfillTaskType } from '@/app/types';
import { cn, getResourceUrl, normalizeDiscreteVideoDurationToSeconds, reportEvent } from '@/app/utils';
import { usePathname, useRouter } from '@/i18n/routing';
import { getAvailableVideoApplyTargets, getVideoApplyDuration } from './VideoApplyButton.utils';

interface VideoApplyButtonProps {
  className?: string;
  creation: CreationInfo | CreationWithTask;
  content?: ReactElement;
  container?: HTMLElement | null;
  titleText?: string;
}

const createVideoPromptFromCreation = (creation: CreationInfo | CreationWithTask): ToolTaskFormVideoPrompt => {
  const previewVideoUrl = getResourceUrl({
    uri: creation.uri || '',
    noMarkUri: creation.nomark_uri || '',
  });

  return {
    id: creation.id || '',
    status: 'done',
    prompt: {
      video: creation.uri || '',
      videoPreview: previewVideoUrl,
      duration: creation.duration,
      coverImage: creation.cover_uri,
      coverImagePreview: creation.cover_uri,
      resolution: creation.resolution,
    } as ToolTaskFormVideoPrompt['prompt'] & {
      resolution?: {
        width: number;
        height: number;
        resolution: string;
      };
    },
    reference: {
      creation_id: creation.id || '',
    },
  };
};

export const VideoApplyButton: FC<VideoApplyButtonProps> = ({ className, creation, content, container, titleText }) => {
  const t = useTranslations();
  const pathname = usePathname();
  const router = useRouter();
  const { supportsHover } = useDevice();
  const [selectedType, setSelectedType] = useState<BackfillTaskType>(BackfillTaskType.LIP_SYNC);
  const [isNonHoverCardOpen, setIsNonHoverCardOpen] = useState(false);
  const {
    lipSyncSettings,
    faceSwapSettings,
    motionControlSettings,
    updateLipSyncFormParams,
    updateFaceSwapFormParams,
    updateMotionControlFormParams,
  } = useStore((state) => ({
    lipSyncSettings: state.lipSyncFormParams.settings,
    faceSwapSettings: state.faceSwapFormParams.settings,
    motionControlSettings: state.motionControlFormParams.settings,
    updateLipSyncFormParams: state.updateLipSyncFormParams,
    updateFaceSwapFormParams: state.updateFaceSwapFormParams,
    updateMotionControlFormParams: state.updateMotionControlFormParams,
  }));

  const duration = useMemo(() => getVideoApplyDuration(creation), [creation]);
  const availableTargets = useMemo(() => getAvailableVideoApplyTargets(duration), [duration]);

  const options = useMemo(
    () =>
      availableTargets.map((target) => ({
        label: t(target.labelKey as Parameters<typeof t>[0]),
        key: target.key,
      })),
    [availableTargets, t],
  );

  useEffect(() => {
    if (supportsHover) {
      setIsNonHoverCardOpen(false);
    }
  }, [supportsHover]);

  useEffect(() => {
    if (!availableTargets.some((target) => target.key === selectedType)) {
      setSelectedType(availableTargets[0]?.key ?? BackfillTaskType.LIP_SYNC);
    }
  }, [availableTargets, selectedType]);

  const applyVideoPrompt = (type: BackfillTaskType, videoPrompt: ToolTaskFormVideoPrompt) => {
    const normalizedDuration = normalizeDiscreteVideoDurationToSeconds(duration);

    if (type === BackfillTaskType.LIP_SYNC) {
      updateLipSyncFormParams('videoPrompts', [videoPrompt]);
      updateLipSyncFormParams('settings', { ...lipSyncSettings, duration: normalizedDuration });
      return;
    }

    if (type === BackfillTaskType.FACE_SWAP) {
      updateFaceSwapFormParams('videoPrompts', [videoPrompt]);
      updateFaceSwapFormParams('settings', { ...faceSwapSettings, duration: normalizedDuration });
      return;
    }

    updateMotionControlFormParams('videoPrompts', [videoPrompt]);
    updateMotionControlFormParams('settings', { ...motionControlSettings, duration: normalizedDuration });
  };

  const handleApply = (type = selectedType) => {
    const target = availableTargets.find((item) => item.key === type) || availableTargets[0];
    if (!target) return;

    const videoPrompt = createVideoPromptFromCreation(creation);
    applyVideoPrompt(type, videoPrompt);
    reportEvent('apply_click', {
      apply_type: type,
      apply_position: 'create_task_card',
    });

    if (pathname.endsWith(target.path)) return;

    router.push(target.path);
  };

  if (availableTargets.length === 0) {
    return null;
  }

  const handleTriggerClick = () => {
    if (!supportsHover) {
      setIsNonHoverCardOpen((prev) => !prev);
      return;
    }

    handleApply();
  };

  const renderTrigger = () => {
    if (content) {
      const contentElement = content as ReactElement<{ onClick?: (e: React.MouseEvent) => void }>;
      return cloneElement(contentElement, {
        onClick: (event: React.MouseEvent) => {
          contentElement.props.onClick?.(event);
          handleTriggerClick();
        },
      });
    }

    return (
      <div
        className={cn('flex cursor-pointer items-center justify-center rounded-8', className)}
        onClick={handleTriggerClick}
      >
        <ProductLineApply1 />
      </div>
    );
  };

  return (
    <HoverCard
      openDelay={0}
      closeDelay={100}
      open={supportsHover ? undefined : isNonHoverCardOpen}
      onOpenChange={supportsHover ? undefined : setIsNonHoverCardOpen}
    >
      <HoverCardTrigger asChild>{renderTrigger()}</HoverCardTrigger>
      <HoverCardPortal container={container || document.body}>
        <HoverCardContent className="w-fit bg-system-bg02 p-3 text-sm" align="center" sideOffset={2} side="top">
          <BackfillTargetOption
            selectedType={selectedType}
            options={options}
            onApply={(type) => {
              setSelectedType(type);
              handleApply(type);
              if (!supportsHover) {
                setIsNonHoverCardOpen(false);
              }
            }}
            titleText={titleText}
          />
          <HoverCardArrow />
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
};
